/**
 * signed-copy-failure.ts — telling "we could not reach Primelane" from "Primelane said no".
 *
 * PURE MODULE: no fetch, no supabase client, no DOM.
 *
 * ── WHY THE SPLIT EARNS ITS OWN FILE ──────────────────────────────────────────
 * `my_signed_copy` is now the ONLY route to an executed agreement's bytes — the storage
 * address is not mintable by a driver any more — so the sentence this app shows when that
 * call fails is the whole of what a driver learns. Two failures wear the same shape and mean
 * opposite things:
 *
 *   TRANSPORT  the request never reached the function: a cold start that timed out, a phone
 *              between cells, a tunnel that dropped. NOTHING was decided about this document.
 *              Retrying is exactly the right move, and telling this driver their copy "is not
 *              ready" would be a statement the server never made.
 *   STATE      the function answered, and the answer was no: not finalized yet, not this
 *              driver's document, or (once fleet-backend's witness gate lands) a witness
 *              agreement whose witness has not signed. Retrying re-asks a settled question.
 *
 * ── DUCK-TYPED ON `name`, DELIBERATELY ────────────────────────────────────────
 * `@supabase/supabase-js` exports `FunctionsFetchError` / `FunctionsHttpError` classes, and
 * `instanceof` against them would drag the client — and therefore `import.meta.env` — into a
 * node test runner for a two-branch decision. The class NAME is part of that library's
 * public surface and is what it sets on every instance, so the branch reads it directly. A
 * bare `TypeError: Failed to fetch` is included because that is what a dead `fetch` rejects
 * with when the wrapper is bypassed.
 */

export type SignedCopyFailureKind = 'transport' | 'state';

export interface SignedCopyFailure {
    kind: SignedCopyFailureKind;
    /** True only for the failure a second attempt could actually change. */
    retryable: boolean;
    title: string;
    body: string;
}

/** The names `functions.invoke` rejects with when the request never reached a verdict. */
const TRANSPORT_NAMES = new Set(['FunctionsFetchError', 'FunctionsRelayError']);

function errorName(error: unknown): string {
    const name = (error as { name?: unknown } | null)?.name;
    return typeof name === 'string' ? name : '';
}

function errorMessage(error: unknown): string {
    const message = (error as { message?: unknown } | null)?.message;
    return typeof message === 'string' ? message : '';
}

export function isTransportFailure(error: unknown): boolean {
    const name = errorName(error);
    if (TRANSPORT_NAMES.has(name)) return true;
    // A raw `fetch` rejection, which is what a `TypeError` with this message always is.
    return name === 'TypeError' && /failed to fetch|network|load failed/i.test(errorMessage(error));
}

export function signedCopyFailure(error: unknown): SignedCopyFailure {
    if (isTransportFailure(error)) {
        return {
            kind: 'transport',
            retryable: true,
            title: 'We could not reach Primelane',
            body: 'Your agreement is fine — the connection was not. Check your connection and try again.',
        };
    }
    return {
        kind: 'state',
        retryable: false,
        title: 'This copy is not available yet',
        body: 'Your agreement appears here once every party has signed it. Contact Primelane if you think it should be ready.',
    };
}
